import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { ReactNode } from 'react';

import { Navigate } from '@sorare/routing';

import { HighlightOverlay } from '@sorare/core/src/components/onboarding/HighlightOverlay';
import { FOOTBALL_PLAY_PRO_ONBOARDING_CLUB_SELECTION } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';

import { useComposeTeamOnboardFlow } from '@sorare/gameplay/src/lib/compose-team';

import type { WithOnboardingComponents_currentUser } from './__generated__/index.graphql';

type Props = {
  children: ReactNode;
  currentUser: Nullable<WithOnboardingComponents_currentUser>;
};

export const WithOnboardingComponents = ({
  children,
  currentUser: currentUserProp,
}: Props) => {
  const composeTeamOnboardFlow = useComposeTeamOnboardFlow();
  const {
    flags: {
      useSelectFavoriteClubOnboarding = false,
      useCommonGameOnboarding = false,
    },
  } = useFeatureFlags();

  const { currentUser } = useCurrentUserContext();

  if (useCommonGameOnboarding && currentUser?.forcedIntoOnboarding) {
    if (currentUserProp?.footballUserProfile?.commonGameOnboarded) {
      return (
        <>
          <HighlightOverlay highlightNames={['PlayPro']} disableBackdropClose />
          {children}
        </>
      );
    }
    return (
      <Navigate to={FOOTBALL_PLAY_PRO_ONBOARDING_CLUB_SELECTION} replace />
    );
  }

  if (useSelectFavoriteClubOnboarding && currentUser?.forcedIntoOnboarding) {
    return (
      <Navigate to={FOOTBALL_PLAY_PRO_ONBOARDING_CLUB_SELECTION} replace />
    );
  }

  return (
    <>
      {composeTeamOnboardFlow === 'compose_first_team' &&
        currentUser?.forcedIntoOnboarding && (
          <HighlightOverlay
            highlightNames={['PlayBanners']}
            disableBackdropClose
          />
        )}
      {children}
    </>
  );
};

WithOnboardingComponents.fragments = {
  currentUser: gql`
    fragment WithOnboardingComponents_currentUser on CurrentUser {
      slug
      id
      footballUserProfile {
        id
        commonGameOnboarded
      }
    }
  ` as TypedDocumentNode<WithOnboardingComponents_currentUser>,
};
